import { Group } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import useTheme from "@mui/material/styles/useTheme";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AssetMasterDataTab from "./components/AssetMasterDataTab";
import AssetSettingsTab from "./components/AssetSettingsTab";
import AssetsOverviewGrid from "./components/AssetsOverviewGrid";
import { useAssetForm } from "./hooks/useAssetForm";
import {
  buildAssetFormFromDevice,
  EMPTY_ASSET_FORM,
  hasDeviceChanges,
  shouldHydrateForm,
  toAssetFormValues,
} from "./utils/assetEntity";
import { ASSET_TYPE_GROUPS } from "./utils/assetTypes";
import { saveAsset, setDeviceSerialNumber } from "./utils/assetWrites";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { FilterStackLayout } from "@/components/layout/FilterStackLayout";
import { useDeviceById } from "@/hooks/devices/useDeviceById";
import { useGroups } from "@/hooks/groups/useGroups";
import { useGroupsByParent } from "@/hooks/groups/useGroupsByParent";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { DeviceSelect } from "@/pages/live-data/components/DeviceSelect";
import { getErrorMessage } from "@/utilities/error/getErrorMessage";

const NO_GROUPS: Group[] = [];

type AssetsTab = "asset" | "deviceSettings";

type PageView = "overview" | "editor";

export default function AssetsPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(700));
  const queryClient = useQueryClient();
  const { geotabClient } = useGeotabClient();

  const [pageView, setPageView] = useState<PageView>("overview");
  const [selectedDevice, setSelectedDevice] = useState<DeviceWithVehicleInfo | null>(null);
  const [isCreatingNewAsset, setIsCreatingNewAsset] = useState(false);
  const { form, setField, reset: resetForm } = useAssetForm();
  const [activeTab, setActiveTab] = useState<AssetsTab>("asset");
  const { data: allGroups = NO_GROUPS, error: groupsError } = useGroups();
  const { data: assetTypeGroupsData = NO_GROUPS, error: assetTypeGroupsError } = useGroupsByParent("Asset Type");
  const groupsLoadError = groupsError ?? assetTypeGroupsError;
  const assetTypeGroups = useMemo(
    () =>
      ASSET_TYPE_GROUPS.map(groupType =>
        assetTypeGroupsData.find(group => group.name === groupType.backendName),
      ).filter((group): group is Group => !!group),
    [assetTypeGroupsData],
  );
  const lastHydrationKeyRef = useRef("");

  const { data: fullDevice, isFetching: isFetchingDeviceDetails, isError: isDeviceDetailsError, error: deviceDetailsError } = useDeviceById(selectedDevice?.id);
  const editableDevice = (fullDevice ?? selectedDevice) as DeviceWithVehicleInfo | null;
  const showAssetEditor = isCreatingNewAsset || !!editableDevice;

  const hydration = useMemo(
    () => buildAssetFormFromDevice(editableDevice, allGroups, assetTypeGroups),
    [allGroups, assetTypeGroups, editableDevice],
  );

  useEffect(() => {
    if (!shouldHydrateForm({ isCreatingNewAsset, lastKey: lastHydrationKeyRef.current, nextKey: hydration.key })) {
      return;
    }
    lastHydrationKeyRef.current = hydration.key;
    resetForm(hydration.form);
  }, [hydration, isCreatingNewAsset, resetForm]);

  const formValues = useMemo(() => toAssetFormValues(form), [form]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedDevice?.id && !isCreatingNewAsset) throw new Error("No device selected");
      return saveAsset(geotabClient, {
        existingId: selectedDevice?.id ?? null,
        form: formValues,
      });
    },
    onError: error => {
      console.error("Error saving asset:", error);
    },
    onSuccess: updatedDevice => {
      setSelectedDevice(updatedDevice.id ? updatedDevice : null);
      setIsCreatingNewAsset(false);
      queryClient.setQueriesData({ queryKey: ["device"] }, cached => {
        if (!cached || typeof cached !== "object") return cached;
        const cachedRecord = cached as Record<string, unknown>;
        return cachedRecord.id === updatedDevice.id ? updatedDevice : cached;
      });
      queryClient.invalidateQueries({ queryKey: ["devicesBySearch"] });
      queryClient.invalidateQueries({ queryKey: ["device"] });
    },
  });

  const hasChanges = useMemo(() => hasDeviceChanges(editableDevice, formValues), [editableDevice, formValues]);

  const canSave =
    form.name.trim().length > 0 &&
    hasChanges &&
    !saveMutation.isPending &&
    (isCreatingNewAsset || (!!fullDevice && !isFetchingDeviceDetails)) &&
    (!isCreatingNewAsset || (!!form.assetTypeGroup && form.serialNumber.trim().length > 0));
  const isDeviceActive = !editableDevice?.activeTo || new Date(editableDevice.activeTo) > new Date();
  const currentSerialNumber = editableDevice?.serialNumber ?? "";
  const canEditSerialNumber = !!selectedDevice?.id && !!fullDevice && !isFetchingDeviceDetails;

  const deviceTypeLabel = useMemo(() => {
    const currentDeviceType = (editableDevice as unknown as Record<string, unknown>)?.deviceType;
    if (!currentDeviceType) return "-";
    if (typeof currentDeviceType === "string") return currentDeviceType;

    const record = currentDeviceType as Record<string, unknown>;
    return String(record.name ?? record.id ?? "-");
  }, [editableDevice]);

  const serialNumberMutation = useMutation({
    mutationFn: async (nextSerialNumber?: string) => {
      if (!selectedDevice?.id) throw new Error("No device selected");
      await setDeviceSerialNumber(geotabClient, selectedDevice.id, nextSerialNumber);
    },
    onSuccess: (_, nextSerialNumber) => {
      const updatedSerialNumber = nextSerialNumber ?? "";
      setField("serialNumber", updatedSerialNumber);
      setSelectedDevice(prev => (prev ? { ...prev, serialNumber: updatedSerialNumber } : prev));
      queryClient.invalidateQueries({ queryKey: ["devicesBySearch"] });
      queryClient.invalidateQueries({ queryKey: ["device"] });
    },
  });

  const resetMutations = () => {
    saveMutation.reset();
    serialNumberMutation.reset();
  };

  const startNewAsset = () => {
    resetMutations();
    setIsCreatingNewAsset(true);
    setSelectedDevice(null);
    lastHydrationKeyRef.current = "new-asset";
    resetForm({
      ...EMPTY_ASSET_FORM,
      assetTypeGroup: assetTypeGroups.find(group => group.name === "Vehicle") ?? assetTypeGroups[0] ?? null,
    });
    setActiveTab("asset");
    setPageView("editor");
  };

  const cancelNewAsset = () => {
    resetMutations();
    setIsCreatingNewAsset(false);
    setSelectedDevice(null);
    lastHydrationKeyRef.current = "";
    resetForm();
    setActiveTab("asset");
    setPageView("overview");
  };

  return (
    <Stack spacing={0} sx={pageView === "overview" ? { height: "100%" } : undefined}>
      {/* Page-level tab bar: Overview vs. Editor */}
      <Paper square elevation={0} sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Tabs
            value={pageView}
            onChange={(_, value: PageView) => setPageView(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Tab label={t("assets.overview.tab")} value="overview" />
            <Tab label={t("assets.editorTab")} value="editor" />
          </Tabs>
          <Button variant="outlined" size="small" onClick={startNewAsset} sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
            {t("assets.newAsset")}
          </Button>
        </Stack>
      </Paper>

      {pageView === "overview" && (
        <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden", p: 2 }}>
          <AssetsOverviewGrid
            onSelectDevice={device => {
              resetMutations();
              setSelectedDevice(device as DeviceWithVehicleInfo);
              setIsCreatingNewAsset(false);
              setPageView("editor");
            }}
          />
        </Box>
      )}

      {pageView === "editor" && (
        <FilterStackLayout
          isMobile={isMobile}
          minHeight={68}
          filters={
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                width: "100%",
                gap: 2,
                alignItems: isMobile ? "stretch" : "center",
              }}
            >
              <DeviceSelect
                includeWithoutSerial
                value={selectedDevice}
                onChange={device => {
                  resetMutations();
                  setSelectedDevice(device);
                  if (device) setIsCreatingNewAsset(false);
                }}
              />
              {isCreatingNewAsset && (
                <Button variant="text" color="inherit" onClick={cancelNewAsset} sx={{ whiteSpace: "nowrap" }}>
                  {t("assets.cancelNewAsset")}
                </Button>
              )}
            </Box>
          }
          content={
            <Box sx={{ p: 2 }}>
              {groupsLoadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t("assets.groupsLoadError")} {getErrorMessage(groupsLoadError)}
                </Alert>
              )}

              {!showAssetEditor && <Alert severity="info">{t("assets.selectAssetHint")}</Alert>}

              {showAssetEditor && (
                <Stack spacing={2}>
                  {isCreatingNewAsset && <Alert severity="info">{t("assets.newAssetHint")}</Alert>}

                  <Paper sx={{ px: 2, py: 1 }}>
                    <Tabs
                      value={activeTab}
                      onChange={(_, value: AssetsTab) => setActiveTab(value)}
                      variant={isMobile ? "scrollable" : "standard"}
                      scrollButtons="auto"
                    >
                      <Tab label={t("assets.tabs.asset")} value="asset" />
                      <Tab label={t("assets.tabs.deviceSettings")} value="deviceSettings" />
                    </Tabs>
                  </Paper>

                  {activeTab === "asset" && (
                    <AssetMasterDataTab
                      form={form}
                      setField={setField}
                      assetTypeGroups={assetTypeGroups}
                      allGroups={allGroups}
                      isEditingExisting={!!selectedDevice}
                      deviceTypeLabel={deviceTypeLabel}
                      isFetchingDetails={isFetchingDeviceDetails}
                      detailsError={deviceDetailsError}
                      isDetailsError={isDeviceDetailsError}
                      saveError={saveMutation.error}
                      isSaveError={saveMutation.isError}
                      showSaveSuccess={saveMutation.isSuccess && !hasChanges}
                      isSaving={saveMutation.isPending}
                      canSave={canSave}
                      onSave={() => saveMutation.mutate()}
                    />
                  )}

                  {activeTab === "deviceSettings" && (
                    <AssetSettingsTab
                      device={editableDevice}
                      deviceTypeLabel={deviceTypeLabel}
                      isDeviceActive={isDeviceActive}
                      key={currentSerialNumber}
                      currentSerialNumber={currentSerialNumber}
                      canEditSerialNumber={canEditSerialNumber}
                      isSerialPending={serialNumberMutation.isPending}
                      serialError={serialNumberMutation.error}
                      isSerialError={serialNumberMutation.isError}
                      onSwap={serialNumber => serialNumberMutation.mutate(serialNumber)}
                      onRemove={() => serialNumberMutation.mutate(undefined)}
                      isMobile={isMobile}
                    />
                  )}
                </Stack>
              )}
            </Box>
          }
        />
      )}
    </Stack>
  );
}
